import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';
import { textVariantClasses } from './Text';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { ptBR } from 'react-day-picker/locale';

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      locale={ptBR}
      showOutsideDays={showOutsideDays}
      className={clsx(
        'bg-base-white group/calendar p-4xs rounded-md [--cell-size:38px]',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout="label"
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: clsx('w-[276px]', defaultClassNames.root),
        months: clsx('flex gap-md flex-col relative', defaultClassNames.months),
        month: clsx('flex flex-col w-full gap-sm', defaultClassNames.month),
        nav: clsx(
          'flex items-center w-full absolute top-0 inset-x-0 justify-between',
          defaultClassNames.nav
        ),
        button_previous: clsx(
          'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none flex items-center justify-center hover:bg-neutral-subtle transition-all rounded-md cursor-pointer outline-none',
          defaultClassNames.button_previous
        ),
        button_next: clsx(
          'size-(--cell-size) aria-disabled:opacity-50 p-0 select-none  flex items-center justify-center hover:bg-neutral-subtle transition-all rounded-md cursor-pointer outline-node',
          defaultClassNames.button_next
        ),
        month_caption: clsx(
          'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)',
          defaultClassNames.month_caption
        ),
        caption_label: clsx(
          'select-none text-neutral-muted',
          textVariantClasses['label-sm-strong'],
          defaultClassNames.caption_label
        ),
        table: 'w-full border-collapse',
        weekdays: clsx('flex', defaultClassNames.weekdays),
        weekday: clsx(
          'text-neutral-muted flex-1 select-none',
          textVariantClasses['label-xs-strong'],
          defaultClassNames.weekday
        ),
        week: clsx('flex w-full', defaultClassNames.week),
        week_number_header: clsx(
          'select-none w-(--cell-size)',
          defaultClassNames.week_number_header
        ),
        week_number: clsx(
          'text-[0.8rem] select-none text-muted-foreground',
          defaultClassNames.week_number
        ),
        day: clsx(
          'relative w-full h-full p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none',
          defaultClassNames.day
        ),
        range_start: clsx('rounded-l-md bg-accent', defaultClassNames.range_start),
        range_middle: clsx('rounded-none', defaultClassNames.range_middle),
        range_end: clsx('rounded-r-md bg-accent', defaultClassNames.range_end),
        today: clsx('bg-neutral-subtle rounded-full', defaultClassNames.today),
        outside: clsx(
          'text-neutral-subtle aria-selected:text-neutral-subtle',
          defaultClassNames.outside
        ),
        disabled: clsx('text-muted-foreground opacity-50', defaultClassNames.disabled),
        hidden: clsx('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={clsx('size-4', className)} {...props} />;
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={clsx('size-4', className)} {...props} />;
          }

          return <ChevronDownIcon className={clsx('size-4', className)} {...props} />;
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = useRef<HTMLButtonElement>(null);

  const defaultClassNames = getDefaultClassNames();

  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={clsx(
        'hover:bg-neutral-subtle inline-flex cursor-pointer items-center justify-center transition-all duration-150 ease-in-out',
        'data-[selected-single=true]:bg-brand-default data-[selected-single=true]:text-base-white data-[range-middle=true]:bg-neutral-subtle data-[range-middle=true]:text-neutral-default data-[range-start=true]:bg-brand-default data-[range-start=true]:text-base-white data-[range-end=true]:bg-brand-default data-[range-end=true]:text-base-white flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-l-md data-[selected-single=true]:rounded-md',
        textVariantClasses['label-sm-default'],
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
}
